import type { ApiProductDetail, ExpressCheckResult } from '../api/types'
import { EXPRESS_SHIPMENT_MODE, EXPRESS_UI } from '../constants/express'
import {
  CHECKOUT_UI,
  ORDER_PATHS,
  type CheckoutAddonOption,
  type CheckoutLineAddons,
  type CheckoutShipmentGroup,
} from '../constants/orders'
import {
  CHECKOUT_SPLIT_MODE,
  SCHEDULE_UI,
  type CheckoutEstimate,
  type CheckoutSplitMode,
} from '../constants/schedule'
import type { CartLine } from './cart'
import { isValidPincode } from './location'
import { primaryImageUrl } from './mapCatalog'

export function isCheckoutRoute(pathname: string): boolean {
  return pathname === ORDER_PATHS.checkout
}

/** Saved addresses that share the delivery area's pincode; none until the pincode is known. */
export function addressesWithPincode<T extends { pincode: string | null }>(
  addresses: T[],
  pincode: string | null | undefined,
): T[] {
  const needle = pincode?.trim()
  if (!needle || !isValidPincode(needle)) return []
  return addresses.filter((address) => address.pincode?.trim() === needle)
}

export function uniqueCartProductSlugs(lines: CartLine[]): string[] {
  return [...new Set(lines.map((line) => line.productSlug).filter(Boolean))]
}

export function checkoutLineMeta(line: CartLine): string[] {
  return [
    line.variantLabel,
    line.wrapperLabel,
    ...line.addonLabels,
  ].filter((part): part is string => Boolean(part && part.trim()))
}

export function remainingAddonsForLine(
  line: CartLine,
  product: ApiProductDetail | undefined,
): CheckoutAddonOption[] {
  if (!product) return []
  return (product.addons ?? [])
    .filter((addon) => !line.addonIds.includes(addon.id))
    .map((addon) => ({
      id: addon.id,
      label: addon.name,
      price: addon.displayPrice,
      imageUrl: primaryImageUrl(addon.images),
    }))
}

export function checkoutLineAddons(
  lines: CartLine[],
  productsBySlug: Map<string, ApiProductDetail>,
): CheckoutLineAddons[] {
  return lines
    .map((line) => ({
      lineId: line.id,
      options: remainingAddonsForLine(line, productsBySlug.get(line.productSlug)),
    }))
    .filter((entry) => entry.options.length > 0)
}

export function addonsForCheckoutLine(
  entries: CheckoutLineAddons[],
  lineId: string,
): CheckoutAddonOption[] {
  return entries.find((entry) => entry.lineId === lineId)?.options ?? []
}

export function checkoutCanSplit(express: ExpressCheckResult | null): boolean {
  return express?.canSplit === true
}

export function checkoutCanSchedule(
  shipments: CheckoutShipmentGroup[],
  splitMode: CheckoutSplitMode,
  checking = false,
): boolean {
  if (checking) return false
  if (splitMode === CHECKOUT_SPLIT_MODE.combined) return shipments.length > 0
  return shipments.some((group) => group.mode === EXPRESS_SHIPMENT_MODE.standard)
}

export function checkoutCanScheduleExpress(
  shipments: CheckoutShipmentGroup[],
  splitMode: CheckoutSplitMode,
  checking = false,
): boolean {
  if (checking || splitMode === CHECKOUT_SPLIT_MODE.combined) return false
  return shipments.some((group) => group.mode === EXPRESS_SHIPMENT_MODE.express)
}

export function checkoutDeliveryEstimate(input: {
  checking: boolean
  canSplit: boolean
  splitMode: CheckoutSplitMode
  hasExpress: boolean
  hasStandard: boolean
}): CheckoutEstimate {
  if (input.checking) {
    return {
      title: SCHEDULE_UI.estimateChecking,
      lead: SCHEDULE_UI.estimateCheckingLead,
      tone: 'checking',
    }
  }
  if (input.splitMode === CHECKOUT_SPLIT_MODE.combined || !input.hasExpress) {
    return {
      title: SCHEDULE_UI.estimateStandard,
      lead: SCHEDULE_UI.estimateStandardLead,
      tone: 'standard',
    }
  }
  if (input.hasExpress && input.hasStandard) {
    return {
      title: SCHEDULE_UI.estimateSplit,
      lead: input.canSplit ? SCHEDULE_UI.estimateSplitLead : SCHEDULE_UI.estimateSplitLeadFixed,
      tone: 'split',
    }
  }
  return {
    title: SCHEDULE_UI.planHeading,
    lead: '',
    tone: 'express',
  }
}

export function ungroupedCheckoutShipments(lines: CartLine[]): CheckoutShipmentGroup[] {
  if (lines.length === 0) return []
  return [
    {
      mode: 'items',
      title: CHECKOUT_UI.itemsHeading,
      hint: '',
      lines,
    },
  ]
}

export function checkoutShipmentFlags(shipments: CheckoutShipmentGroup[]): {
  hasExpress: boolean
  hasStandard: boolean
} {
  return {
    hasExpress: shipments.some(
      (group) => group.mode === EXPRESS_SHIPMENT_MODE.express,
    ),
    hasStandard: shipments.some(
      (group) => group.mode === EXPRESS_SHIPMENT_MODE.standard,
    ),
  }
}

export function shipmentItemsLabel(
  shipments: CheckoutShipmentGroup[],
  mode: string,
): string {
  const names = shipments
    .filter((group) => group.mode === mode)
    .flatMap((group) => group.lines.map((line) => line.name))
  return [...new Set(names)].join(', ')
}

export function checkoutAddressRecap(input: {
  address: string
  city: string | null
  pincode: string
}): string {
  const address = input.address.trim()
  const city = input.city?.trim() ?? ''
  const pincode = input.pincode.trim()
  const parts = [address]
  if (city && !address.toLowerCase().includes(city.toLowerCase())) {
    parts.push(city)
  }
  if (pincode && !address.includes(pincode)) parts.push(pincode)
  return parts.filter(Boolean).join(', ')
}

export function lineDeliversExpress(
  line: CartLine,
  express: ExpressCheckResult | null,
): boolean {
  if (line.expressEligible === true) return true
  if (line.expressEligible === false) return false
  const item = express?.items?.find((entry) => entry.variantId === line.variantId)
  if (item) return item.isExpressEligible || item.expressAvailable
  const expressIds =
    express?.groups?.find((group) => group.mode === EXPRESS_SHIPMENT_MODE.express)?.variantIds ??
    []
  return expressIds.includes(line.variantId)
}

/** Cart groups follow each product. A mixed cart stays split, not one standard order. */
export function groupCartDeliveries(
  lines: CartLine[],
  express: ExpressCheckResult | null,
  checking = false,
): CheckoutShipmentGroup[] {
  if (lines.length === 0) return []
  const known = cartDeliveryKnown(lines, express)
  if (!known) {
    return [
      {
        mode: EXPRESS_SHIPMENT_MODE.standard,
        title: checking ? EXPRESS_UI.checkingTitle : EXPRESS_UI.shipmentStandardTitle,
        hint: '',
        lines,
      },
    ]
  }
  return deliveryGroups(
    lines.filter((line) => lineDeliversExpress(line, express)),
    lines.filter((line) => !lineDeliversExpress(line, express)),
    '',
  )
}

export function groupCheckoutShipments(
  lines: CartLine[],
  express: ExpressCheckResult | null,
  splitMode: CheckoutSplitMode = CHECKOUT_SPLIT_MODE.split,
  checking = false,
): CheckoutShipmentGroup[] {
  if (lines.length === 0) return []
  if (splitMode === CHECKOUT_SPLIT_MODE.combined) {
    return [
      {
        mode: EXPRESS_SHIPMENT_MODE.standard,
        title: EXPRESS_UI.shipmentStandardTitle,
        hint: EXPRESS_UI.standardHint,
        lines,
      },
    ]
  }
  const known = cartDeliveryKnown(lines, express)
  if (!known) {
    return [
      {
        mode: EXPRESS_SHIPMENT_MODE.standard,
        title: checking ? EXPRESS_UI.checkingTitle : EXPRESS_UI.shipmentStandardTitle,
        hint: checking ? EXPRESS_UI.checking : EXPRESS_UI.standardHint,
        lines,
      },
    ]
  }
  return deliveryGroups(
    lines.filter((line) => lineDeliversExpress(line, express)),
    lines.filter((line) => !lineDeliversExpress(line, express)),
    standardShipmentHint(express),
  )
}

function cartDeliveryKnown(lines: CartLine[], express: ExpressCheckResult | null): boolean {
  if (lines.some((line) => typeof line.expressEligible === 'boolean')) return true
  if (express?.items?.length) return true
  return Boolean(
    express?.groups?.some(
      (group) => group.mode === EXPRESS_SHIPMENT_MODE.express && group.variantIds.length > 0,
    ),
  )
}

function deliveryGroups(
  expressLines: CartLine[],
  standardLines: CartLine[],
  standardHint: string,
): CheckoutShipmentGroup[] {
  const groups: CheckoutShipmentGroup[] = []
  if (expressLines.length > 0) {
    groups.push({
      mode: EXPRESS_SHIPMENT_MODE.express,
      title: EXPRESS_UI.shipmentExpressTitle,
      hint: EXPRESS_UI.shipmentExpressHint,
      lines: expressLines,
    })
  }
  if (standardLines.length > 0) {
    groups.push({
      mode: EXPRESS_SHIPMENT_MODE.standard,
      title: EXPRESS_UI.shipmentStandardTitle,
      hint: standardHint,
      lines: standardLines,
    })
  }
  return groups
}

function standardShipmentHint(express: ExpressCheckResult | null): string {
  if (express?.canSplit) return EXPRESS_UI.shipmentStandardHint
  return EXPRESS_UI.standardReadyHint
}
